import {Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';

import {TranslatePipe} from "@ngx-translate/core";
import {FormWrapper} from "../items/form.wrapper";
import {AbstractFormFieldComponent} from "../abstract-form-field.component";
import {IconComponent} from "../../icon/icon.component";
import {NgbTooltip} from "@ng-bootstrap/ng-bootstrap";
import {isDefined, unsubscribe} from "../../utils/epic.utils";
import {Size} from "../../../base/data/size";
import {FormMetadata} from "../items/form-metadata";
import {Subscription} from "rxjs";

@Component({
  selector: 'acc-form-label',
  imports: [
    TranslatePipe,
    IconComponent,
    NgbTooltip
  ],
  templateUrl: './form-label.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './form-label.component.css'
})
export class FormLabelComponent extends AbstractFormFieldComponent implements OnInit, OnDestroy {

  @Input({required: true})
  public name!: string;

  @Input({required: true})
  public wrapper!: FormWrapper;

  @Input() // Override required
  public required: boolean = false;

  @Input() // Override help
  public help: string | undefined;

  @Input() // Override warn
  public warn: string | undefined;

  @Input() // Override info
  public info: string | undefined;

  @Input() // Override info
  public forceShow: boolean = false;

  @Input()
  public size: Size | undefined = undefined;

  private readonly subs: Subscription[] = [];
  private translation: string | undefined = undefined;

  ngOnInit(): void {
    const sub: Subscription | undefined = this.wrapper.getMetadataByName(this.name)?.translationSub.asObservable()
      .subscribe((t) => this.translation = t);
    if (isDefined(sub)) {
      this.subs.push(sub);
    }
  }

  ngOnDestroy(): void {
    unsubscribe(this.subs);
    this.translation = undefined;
  }

  protected override isViewOnly(): boolean {
    return this._isViewOnly(this.name, this.wrapper);
  }

  protected showLabel(): boolean {
    const metadata: FormMetadata | undefined = this.wrapper.getMetadataByName(this.name);
    return this.forceShow || metadata?.label !== 'hide';
  }

  protected isRequiredField(): boolean {
    if (this.required) {
      return true;
    }
    return this._isRequiredField(this.name, this.wrapper);
  }

  protected hasWarn(name: string): boolean {
    return !!(this.warn || this.wrapper.hasWarn(name));
  }

  protected hasHelp(name: string): boolean {
    return !!(this.help || this.wrapper.hasHelp(name));
  }

  protected hasInfo(name: string): boolean {
    return !!(this.info || this.wrapper.hasInfo(name));
  }

  protected getWarn(name: string): string {
    return this.warn || this.wrapper.getWarn(name);
  }

  protected getHelp(name: string): string {
    return this.help || this.wrapper.getHelp(name);
  }

  protected getInfo(name: string): string {
    return this.info || this.wrapper.getInfo(name);
  }

  protected isInfoTranslated(name: string): boolean {
    return isDefined(this.info) ? false : this.wrapper.isInfoTranslated(name);
  }

  protected getClass(): string {
    return isDefined(this.size) ? 'form-label-' + this.size.value : '';
  }

  protected getTranslationCode(): string {
    return isDefined(this.translation) ? this.translation : this.wrapper.getTranslationCode(this.name);
  }
}
